import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addMonths, format, parse } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { dateLocaleFor } from '@/i18n';

/** Earliest selectable month — KSeF data doesn't predate this. */
const MIN_MONTH = '2026-01';

/** The current calendar month as "yyyy-MM". */
function currentMonth(): string {
  return format(new Date(), 'yyyy-MM');
}

interface InvoiceMonthValue {
  /** Selected month, "yyyy-MM". */
  month: string;
  /** Set the month; ignored if outside [min, max]. */
  setMonth: (month: string) => void;
  min: string;
  max: string;
}

const InvoiceMonthContext = createContext<InvoiceMonthValue | null>(null);

export function useInvoiceMonth(): InvoiceMonthValue {
  const ctx = useContext(InvoiceMonthContext);
  if (!ctx) throw new Error('useInvoiceMonth must be used within an InvoiceMonthProvider');
  return ctx;
}

/**
 * Holds the month selected across the invoice pages. Mounted in the invoices layout route so the
 * selection survives navigation between Summary / Issued / Received. Defaults to the current month;
 * bounded to [January 2026, current month].
 */
export function InvoiceMonthProvider({ children }: { children: ReactNode }) {
  const max = useMemo(currentMonth, []);
  const [month, setMonthState] = useState(max);

  const value = useMemo<InvoiceMonthValue>(
    () => ({
      month,
      setMonth: (next) => {
        if (next >= MIN_MONTH && next <= max) setMonthState(next);
      },
      min: MIN_MONTH,
      max,
    }),
    [month, max],
  );

  return <InvoiceMonthContext.Provider value={value}>{children}</InvoiceMonthContext.Provider>;
}

/**
 * Prev / "June 2026" / next control, bounded to the provider's [min, max]. The centre label opens a
 * popover with a year-paged month grid and a "Current month" shortcut for jumping back to `max`.
 */
export function MonthPicker() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const locale = dateLocaleFor(lng);
  const { month, setMonth, min, max } = useInvoiceMonth();

  const date = parse(month, 'yyyy-MM', new Date());
  const previous = format(addMonths(date, -1), 'yyyy-MM');
  const next = format(addMonths(date, 1), 'yyyy-MM');

  const selectedYear = Number(month.slice(0, 4));
  const minYear = Number(min.slice(0, 4));
  const maxYear = Number(max.slice(0, 4));

  const [open, setOpen] = useState(false);
  // Which year the grid shows; resets to the selected month's year each time the popover opens.
  const [year, setYear] = useState(selectedYear);

  const pick = (value: string) => {
    setMonth(value);
    setOpen(false);
  };

  return (
    <div className="inline-flex items-center gap-1 rounded-md border p-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('invoices.month.previous')}
        disabled={previous < min}
        onClick={() => setMonth(previous)}
      >
        <ChevronLeft className="size-4" />
      </Button>

      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          if (isOpen) setYear(selectedYear);
          setOpen(isOpen);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            aria-label={t('invoices.month.open')}
            className="min-w-36 text-sm font-medium capitalize tabular-nums"
          >
            {format(date, 'LLLL yyyy', { locale })}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64" align="center">
          <div className="mb-2 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('invoices.month.previous_year')}
              disabled={year <= minYear}
              onClick={() => setYear((y) => y - 1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-sm font-medium tabular-nums">{year}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('invoices.month.next_year')}
              disabled={year >= maxYear}
              onClick={() => setYear((y) => y + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {Array.from({ length: 12 }, (_, i) => {
              const value = `${year}-${String(i + 1).padStart(2, '0')}`;
              return (
                <Button
                  key={value}
                  type="button"
                  variant={value === month ? 'default' : 'ghost'}
                  className="capitalize"
                  disabled={value < min || value > max}
                  onClick={() => pick(value)}
                >
                  {format(new Date(year, i, 1), 'LLL', { locale })}
                </Button>
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            className="mt-2 w-full"
            disabled={month === max}
            onClick={() => pick(max)}
          >
            {t('invoices.month.current')}
          </Button>
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('invoices.month.next')}
        disabled={next > max}
        onClick={() => setMonth(next)}
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
}
